import {
  CSSProperties,
  useEffect,
  useRef,
  useState,
} from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import { CalendarPage, MonthPage } from '../calendars/CalendarMonth';
import { useDispatch } from 'react-redux';
import { changeFace, changeRatio } from '../../constants/initialStore';
import { Calendar } from '../../types/models/Calendar';
import { fetchDispatchCalendar } from '../../hook/useFetchOrCreateCalendar';
import { fetchDispatchCard } from '../../hook/useFetchOrCreateCard';
import DrawZone from '../../components/cardElements/DrawZone';
import { ConvertMMToPixel } from '../../utils/convertCMToPixel';
import { DndProvider } from 'react-dnd';
import { HTML5Backend } from 'react-dnd-html5-backend';
import useImportTextFonts from '../../hook/useImportTextFonts';
import styled from 'styled-components';
import OtherDrawZone from '../others/OtherDrawZone';
import { otherFormats } from '../../constants/otherFormats';
import { Page } from '../albums/AlbumPage';
import { Album } from '../../types/models/Album';
import { fetchDispatchOther } from '../../hook/useFecthOrCreateOther';
import { fetchAlbum } from '../../hook/useFetchOrCreateAlbum';
import { LoginState, useAuth } from '../../hook/useAuth';
import { CardFromServer } from '../../types/models/Cards';
import { Other } from '../../types/models/Other';
import { formats } from '../../constants/formats';
import { Slice } from '../../components/functionnalities/creationPreview/albumPreview/CoverPreview';
import { calendarFormats } from '../../constants/calendarFormats';

export const GenerationPage = () => {
  const { id, type } = useParams();
  const dispatch = useDispatch();
  const [searchParams] = useSearchParams();
  const pageNumber = parseInt(searchParams.get('page') ?? '') || 0;
  const signature = searchParams.get('signature');
  const [data, setData] = useState<
    Album | CardFromServer | Calendar | Other | null
  >(null);

  const { userInfo, isReady, uuidInCookie } = useAuth();

  const maxWidth = parseInt(searchParams.get('maxWidth') ?? '') || 0;
  const preview = Boolean(parseInt(searchParams.get('preview') ?? '0') || 0);
  const showCutZonesInOrderPreview = preview && !signature;
  
  let defaultWrapperStyles = {};
  if (preview) {
    defaultWrapperStyles = {
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
    }
  }
  const wrapperRef = useRef<HTMLDivElement | null>(null);
  const contentRef = useRef<HTMLDivElement | null>(null);
  const [wrapperStyles, setWrapperStyles] = useState<
    CSSProperties | undefined
  >(defaultWrapperStyles);
  const [isScaleRecalculating, setIsScaleRecalculating] = useState(false);
  const scaleCycleRef = useRef(0);

  useImportTextFonts();

  useEffect(() => {
    const hasAccessContext =
      Boolean(uuidInCookie) || userInfo?.state === LoginState.LOGGED_IN;
    const canLoadWithSignature = Boolean(signature);
    const canLoadWithAuth = isReady && hasAccessContext;
    const isPreviewWithoutSignature = preview && !canLoadWithSignature;
    const canLoadPreview = userInfo?.state === LoginState.LOGGED_IN;

    const getData = async () => {
      let dataFromServ = null;
      if (id === undefined) return;
      switch (type) {
        case 'calendrier':
          dataFromServ = await fetchDispatchCalendar(id);
          setData(dataFromServ as unknown as Calendar);
          break;
        case 'carte':
          dataFromServ = await fetchDispatchCard(id);
          dispatch(changeFace(pageNumber));
          setData(dataFromServ as unknown as CardFromServer);
          break;
        case 'album':
          dataFromServ = await fetchAlbum(id);
          setData(dataFromServ as unknown as Album);
          break;
        case 'autre':
          dataFromServ = await fetchDispatchOther(id);
          setData(dataFromServ as unknown as Other);
          break;
        default:
          throw new Error('Creation type not found');
      }
    };

    const loadData = async () => {
      try {
        await getData();
      } catch (error: any) {
        if (error?.response?.status === 401) {
          return;
        }
        console.error(error);
      }
    };

    if (!type || !id) return;
    // For preview mode: require auth or a valid signature
    if (preview && !canLoadWithSignature && !canLoadPreview) return;
    void loadData();
    dispatch(changeRatio(1));
  }, [id, type, userInfo, isReady, uuidInCookie, pageNumber, signature, preview]);

  useEffect(() => {
    if (!preview || !wrapperRef.current) {
      return;
    }

    let rafId: number | undefined;
    let timeoutId: number | undefined;

    const applyScale = (cycleId: number) => {
      if (!wrapperRef.current) return;
      const contentElement = contentRef.current;
      if (!contentElement) {
        if (scaleCycleRef.current === cycleId) {
          setIsScaleRecalculating(false);
        }
        return;
      }

      const childComputedStyles = window.getComputedStyle(contentElement);
      const wrapperComputedStyles = window.getComputedStyle(wrapperRef.current);

      const childHeight = parseFloat(childComputedStyles.height);
      const childWidth = parseFloat(childComputedStyles.width);
      const wrapperHeight = parseFloat(wrapperComputedStyles.height);
      const wrapperWidth = parseFloat(wrapperComputedStyles.width);

      if (!childHeight || !childWidth) {
        if (scaleCycleRef.current === cycleId) {
          setIsScaleRecalculating(false);
        }
        return;
      }

      const ratioHeight = wrapperHeight / childHeight;
      const ratioWidth = wrapperWidth / childWidth;

      const nextTransform = `scale(${ratioWidth}, ${ratioHeight})`;
      if (contentElement.style.transform === nextTransform) {
        if (scaleCycleRef.current === cycleId) {
          setIsScaleRecalculating(false);
        }
        return;
      }

      contentElement.style.transformOrigin = '0 0';
      contentElement.style.transform = nextTransform;

      window.requestAnimationFrame(() => {
        if (scaleCycleRef.current === cycleId) {
          setIsScaleRecalculating(false);
        }
      });
    };

    const scheduleScale = () => {
      const cycleId = scaleCycleRef.current + 1;
      scaleCycleRef.current = cycleId;
      setIsScaleRecalculating(true);

      if (rafId !== undefined) {
        window.cancelAnimationFrame(rafId);
      }
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }

      rafId = window.requestAnimationFrame(() => {
        timeoutId = window.setTimeout(() => {
          applyScale(cycleId);
        }, 120);
      });
    };

    scheduleScale();

    const resizeHandler = () => {
      scheduleScale();
    };
    window.addEventListener('resize', resizeHandler);

    const resizeObserver = new ResizeObserver(() => {
      scheduleScale();
    });

    resizeObserver.observe(wrapperRef.current);

    return () => {
      window.removeEventListener('resize', resizeHandler);
      resizeObserver.disconnect();
      if (rafId !== undefined) {
        window.cancelAnimationFrame(rafId);
      }
      if (timeoutId !== undefined) {
        window.clearTimeout(timeoutId);
      }
    };
  }, [data, preview, pageNumber, type]);

  if (!data) return <></>;

  const formatsCalendriersRigide = ['RIGCAR30', 'RIGCAR21', 'RIGA4PAY', 'RIGA5PAY', 'RIGA4POR'];

  const getPaddingOfWrapper = () => {
    // clicadmin seems to stick codebar right next to TOICAR20, so we add padding
    if (data.format === 'TOICAR20') {
      return '20px';
    }
    else if (formatsCalendriersRigide.includes(data.format) && pageNumber == 0) {
      return '0px';
      return '120px';
    }
    else {
      return '0px';
    }
  };

  const getBgColor = () => {
    if (formatsCalendriersRigide.includes(data.format) && pageNumber == 0) {
      return (data as Album).albumCover?.backgroundColor;
    }
    return '';
  }


  return (
    <Wrapper
      ref={wrapperRef}
      id="content"
      style={{
        ...wrapperStyles,
        flexDirection:
          data && ['CFG', 'PFM'].includes(data.format) && !preview ? 'row' : 'column',
        justifyContent: preview ? 'flex-start' : 'center',
        alignItems: preview ? 'flex-start' : 'center',
        padding: getPaddingOfWrapper(),
        backgroundColor: getBgColor()
      }}
    >
      {preview && isScaleRecalculating && (
        <ScaleLoadingOverlay>
          <ScaleLoadingSpinner />
        </ScaleLoadingOverlay>
      )}
      <ContentRoot ref={contentRef} $hidden={isScaleRecalculating} $isPreview={preview}>
      <DndProvider backend={HTML5Backend}>
        {type === 'calendrier' &&
          (pageNumber > 0 || calendarFormats[data.format].noCover ? (
            <MonthPage
              {...(data as Calendar).calendarMonths[
              calendarFormats[data.format].noCover
                ? pageNumber + 1
                : pageNumber
              ]}
              isGeneration={true}
              showCutZonesInGeneration={showCutZonesInOrderPreview}
            />
          ) : (
            <CalendarPage
              {...(data as Calendar).calendarMonths[pageNumber]}
              isCover
              isGeneration={true}
              showCutZonesInGeneration={showCutZonesInOrderPreview}
            />
          ))}
        {type === 'autre' && (
          <OtherDrawZone
            displayHeight={ConvertMMToPixel(otherFormats[data.format].height)}
            displayWidth={ConvertMMToPixel(otherFormats[data.format].width)}
            displayForbiddenZones={showCutZonesInOrderPreview}
            isGeneration={true}
          />
        )}
        {type === 'album' &&
          (pageNumber >= 1 ? (
            <Page
              {...(data as Album).albumPages[pageNumber - 1]}
              isCover={false}
              isLogo={false}
              isGeneration={true}
              showCutZonesInGeneration={showCutZonesInOrderPreview}
            />
          ) : (
            <div style={{ display: 'flex', flexDirection: 'row' }}>

              <Page
                {...(data as Album).albumBackCover}
                isCover={true}
                isLogo={true}
                isGeneration={true}
                showCutZonesInGeneration={showCutZonesInOrderPreview}
                backgroundColor={(data as Album).albumCover?.backgroundColor}

              />
              {data.format.includes('RIG') && (
                <Slice
                  {...(data as Album).albumCover}
                  name={(data as Album).name ?? ''}
                  numberOfPages={(data as Album).albumPages.length + 2}
                  isGeneration={true}
                />
              )}
              <Page
                {...(data as Album).albumCover}
                isCover={true}
                isLogo={false}
                isGeneration={true}
                showCutZonesInGeneration={showCutZonesInOrderPreview}
              />
            </div>
          ))}
        {type === 'carte' &&
          ['CFG', 'PFM'].includes(data.format.toString()) &&
          pageNumber === 1 && !preview && (
            <>
              {['CFG'].includes(data.format.toString()) ? (
                <ScaleFix></ScaleFix>
              ) : null}
              <DrawZone
                isGeneration={true}
                displayHeight={ConvertMMToPixel(
                  formats[data.format.toString() + '_old'].facesSizes[
                    pageNumber + 2
                  ].height + (['CFG'].includes(data.format.toString()) ? 3 : 0),
                )}
                displayWidth={ConvertMMToPixel(
                  formats[data.format.toString() + '_old'].facesSizes[
                    pageNumber + 2
                  ].width + (['CFG'].includes(data.format.toString()) ? 3 : 0),
                )}
                displayBends={false}
                displayForbiddenZones={showCutZonesInOrderPreview}
                faceToDisplay={pageNumber + 2}
                $canOverflow={false}
                $correctionTranslation={
                  ['PF2HB'].includes(data.format.toString())
                    ? [14, 27]
                    : ['CFG'].includes(data.format.toString())
                      ? [17, 17]
                      : [0, 0]
                }
              />
            </>
          )}
        {type === 'carte' && (
          <DrawZone
            isGeneration={true}
            displayHeight={ConvertMMToPixel(
              formats[
                data.format.toString() +
                (data.format.includes('MAG') ? '' : '_old')
              ].facesSizes[pageNumber].height,
            )}
            displayWidth={ConvertMMToPixel(
              formats[
                data.format.toString() +
                (data.format.includes('MAG') ? '' : '_old')
              ].facesSizes[pageNumber].width,
            )}
            displayBends={false}
            displayForbiddenZones={showCutZonesInOrderPreview}
            $canOverflow={false}
          />
        )}
        {type === 'carte' &&
          (['PF2HB'].includes(data.format.toString()) ||
            (['CFG', 'PFM'].includes(data.format.toString()) &&
              pageNumber === 0)) && !preview && (
            <>
              {['PF2HB', 'CFG'].includes(data.format.toString()) ? (
                <ScaleFix></ScaleFix>
              ) : null}
              <DrawZone
                isGeneration={true}
                displayHeight={ConvertMMToPixel(
                  formats[data.format.toString() + '_old'].facesSizes[
                    pageNumber + 2
                  ].height +
                  (['PF2HB', 'CFG'].includes(data.format.toString()) ? 3 : 0),
                )}
                displayWidth={ConvertMMToPixel(
                  formats[data.format.toString() + '_old'].facesSizes[
                    pageNumber + 2
                  ].width +
                  (['PF2HB', 'CFG'].includes(data.format.toString()) ? 3 : 0),
                )}
                displayBends={false}
                displayForbiddenZones={showCutZonesInOrderPreview}
                faceToDisplay={pageNumber + 2}
                $canOverflow={false}
                $correctionTranslation={
                  ['PF2HB'].includes(data.format.toString())
                    ? [14, 27]
                    : ['CFG'].includes(data.format.toString())
                      ? [17, 17]
                      : [0, 0]
                }
              />
            </>
          )}
      </DndProvider>
      </ContentRoot>
    </Wrapper>
  );
};

const Wrapper = styled.div`
  position: relative;
  display: flex;
  justify-content: center;
  width: fit-content;
  height: fit-content;
  background-color: white;
  pointer-events: none;
  transform-origin: top left;

  > div {
    clip-path: none !important;
  }
`;

const ContentRoot = styled.div<{ $hidden: boolean; $isPreview: boolean }>`
  visibility: ${(props) => (props.$hidden ? 'hidden' : 'visible')};
  ${(props) => !props.$isPreview && 'display: contents;'}
`;

const ScaleLoadingOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10001;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(255, 255, 255, 1);
  pointer-events: none;
`;

const ScaleLoadingSpinner = styled.div`
  width: 18px;
  height: 18px;
  border-radius: 50%;
  border: 2px solid #d5d9df;
  border-top-color: #2e7c81;
  animation: scaleSpinnerRotate 0.8s linear infinite;

  @keyframes scaleSpinnerRotate {
    from {
      transform: rotate(0deg);
    }
    to {
      transform: rotate(360deg);
    }
  }
`;

const ScaleFix = styled.div`
  + div > div {
    scale: 1.07;
  }
`;
